import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { CanDisable } from '@modernloop/shared/helper-components';
import { CaretDownIcon, ResetIcon } from '@modernloop/shared/icons';
import { EmployeeId } from '@modernloop/shared/ids';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  accordionClasses,
} from '@mui/material';

import { AdvancedOptions_InterviewPlanFragment, DurationInput, DurationOrgPref } from 'src/generated/mloop-graphql';

import DurationWindowPicker from 'src/components/DurationWindowPicker';

import SmartEmployeeSelect from 'src/entities/Select/SmartEmployeeSelect';

import { useOrgPrefDuration } from 'src/hooks/api/org';
import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

type Fragments = {
  interviewPlan: AdvancedOptions_InterviewPlanFragment | undefined;
};

type Props = {
  readonly?: boolean;
  minSchedulingWindowSecondsNeeded: number;
  onSchedulingWindowChange: (duration: DurationInput | null) => void;
  onExcludedInterviewerChange: (employeeIds: EmployeeId[]) => void;
  onResetToJobStageDefault?: () => void;
};

const AdvancedOptions: FCWithFragments<Fragments, Props> = ({
  interviewPlan,
  readonly,
  minSchedulingWindowSecondsNeeded,
  onSchedulingWindowChange,
  onExcludedInterviewerChange,
  onResetToJobStageDefault,
}) => {
  const { t } = useTranslation();
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const [resettingToJobStageDefault, setResettingToJobStageDefault] = useState(false);

  const { logEvent } = useLogEvent();

  const [orgDefaultSchedulingWindow] = useOrgPrefDuration(DurationOrgPref.DurationDefaultSchedulingWindow);

  let schedulingWindow = interviewPlan?.schedulingWindow;
  if (schedulingWindow === null || schedulingWindow === undefined) {
    schedulingWindow = orgDefaultSchedulingWindow;
  }

  if (!orgBreakImprovementsEnabled) {
    return null;
  }

  const handleResetToJobStageDefault = async () => {
    if (!onResetToJobStageDefault) {
      return;
    }
    logEvent(LoggerEvent.CLIENT_RESET_JOB_STAGE_PLAN, {
      type: 'Advance options - Reset interview plan to job stage default',
    });
    setResettingToJobStageDefault(true);
    try {
      await onResetToJobStageDefault();
    } finally {
      setResettingToJobStageDefault(false);
    }
  };

  return (
    <Accordion
      variant="outlined"
      defaultExpanded
      disableGutters
      sx={{
        backgroundColor: 'transparent',
        border: (theme) => `1px dashed ${theme.palette.borderState.default}`,
        borderRadius: '12px !important',
        [`&.${accordionClasses.root}:before`]: {
          backgroundColor: 'transparent',
        },
      }}
    >
      <AccordionSummary expandIcon={<CaretDownIcon />}>
        <Typography variant="subtitle2" color={readonly ? 'text.secondary' : undefined}>
          {t('hiring-dashboard-interviewers:interviewers.plan.advancedOptions.title')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={1}>
          <Grid container flexDirection="row" alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography color={readonly ? 'text.secondary' : undefined}>
                {t('hiring-dashboard-interviewers:interviewers.plan.advancedOptions.schedulingWindow')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DurationWindowPicker
                disabled={readonly}
                minDurationSecondsNeeded={minSchedulingWindowSecondsNeeded}
                durationSec={schedulingWindow?.seconds}
                onChange={(seconds) => onSchedulingWindowChange({ seconds })}
              />
            </Grid>
          </Grid>

          <Grid container flexDirection="row" alignItems="center">
            <Grid item xs={12} sm={6}>
              <Typography color={readonly ? 'text.secondary' : undefined}>
                {t('hiring-dashboard-interviewers:interviewers.plan.advancedOptions.excludeInterviewers')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <CanDisable disabled={Boolean(readonly)}>
                <SmartEmployeeSelect
                  selectedEmployeeIds={interviewPlan?.excludedEmployees?.map((employee) => employee.id) || []}
                  label=""
                  context={{ enableOptionsWithMissingData: false }}
                  placeholder={t('hiring-dashboard-schedule:schedule.textField.selectInterviewersToExclude')}
                  onChange={({ selectedEmployeeIds }) => {
                    logEvent(LoggerEvent.CLIENT_SMART_OPTIONS_APPLIED, {
                      type: 'Advance options - Select interviewers to exclude',
                    });

                    onExcludedInterviewerChange(selectedEmployeeIds);
                  }}
                />
              </CanDisable>
            </Grid>
          </Grid>

          {!readonly && onResetToJobStageDefault && (
            <Grid flexDirection="row" alignItems="center">
              <Grid item>
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<ResetIcon color={!resettingToJobStageDefault ? 'primary' : undefined} />}
                  disabled={resettingToJobStageDefault}
                  endIcon={resettingToJobStageDefault ? <CircularProgress size={16} color="primary" /> : undefined}
                  onClick={handleResetToJobStageDefault}
                >
                  {t('hiring-dashboard-interviewers:interviewers.plan.resetToDefault')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

AdvancedOptions.fragments = {
  interviewPlan: gql`
    fragment AdvancedOptions_interviewPlan on JobStage {
      id
      schedulingWindow {
        seconds
      }
      excludedEmployees {
        id
      }
    }
  `,
};

export default AdvancedOptions;
